import type { ReactNode } from 'react';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { asDataIndexType, DEFAULT_DATA_INDEX_TYPE, dataIndexItemAddress, type DataIndexItemAddress } from './urls';
import { resumedListPath } from './list-memory';
import { useLocation } from '@tanstack/react-router';

type DataIndexItemResolverProps = {
  /**
   * The `:type` segment. An item URL can name any of the four browsers,
   * including the two an object type stands in front of, which is what
   * separates this from the list resolver.
   */
  segment: string;
  /** The item on a two-level URL, the object type on a three-level one. */
  itemId: string;
  nestedItemId?: string;
  /**
   * What to render for a URL this page cannot serve. The `journeys` twin
   * passes the embedded host: ownership leaves such a URL to that app, so a
   * redirect from here would park the transition on a host nothing mounts.
   */
  fallback?: ReactNode;
  children: (address: DataIndexItemAddress) => ReactNode;
};

/**
 * Resolves an item URL to the family that serves it and the name it asks for.
 *
 * A URL naming a family that cannot carry that shape — an object type on the
 * person browser, or an object attribute with no object type — addresses
 * nothing, and neither does a segment naming no browser at all. The embedded
 * app sent those to the attributes browser, and so does this.
 */
export function DataIndexItemResolver({
  segment,
  itemId,
  nestedItemId,
  fallback,
  children,
}: DataIndexItemResolverProps) {
  const type = asDataIndexType(segment);
  const address = type == null ? null : dataIndexItemAddress(type, itemId, nestedItemId);
  const { searchStr } = useLocation();
  const fallbackHref = useMigrationHandoffHref((workspaceId) =>
    resumedListPath(workspaceId, DEFAULT_DATA_INDEX_TYPE, searchStr),
  );

  if (address == null) {
    if (fallback !== undefined) {
      return <>{fallback}</>;
    }
    return fallbackHref == null ? null : (
      <SafeNavigate to={fallbackHref} options={{ shouldReplaceHistoryEntry: true }} />
    );
  }

  return <>{children(address)}</>;
}
